import { Box, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";

import { useDevice } from "@/hooks/useDevice";
import theme from "@/theme";

import { CustomButton } from "./Button";
import { StyledModal } from "./StyledModal";

const useStyles = makeStyles(() => ({
  content: {
    padding: 24,
    display: "flex",
    flexDirection: "column",
    gap: 16,
    [theme.breakpoints.up("md")]: { padding: 32, gap: 24 },
  },
  head: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    textAlign: "center",
    [theme.breakpoints.up("md")]: { textAlign: "left", gap: 4, padding: "8px 0" },
  },
  title: { fontSize: 16, fontWeight: 700, lineHeight: 1.3, [theme.breakpoints.up("md")]: { fontSize: 20 } },
  description: { fontSize: 14, lineHeight: 1.5, whiteSpace: "pre-line", color: theme.palette.text.secondary },
  footnote: { fontSize: 12, lineHeight: 1.5, textAlign: "center", color: theme.palette.text.secondary },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    [theme.breakpoints.up("md")]: { gap: 8, paddingTop: 8, width: 360, maxWidth: "100%", alignSelf: "center" },
  },
  destructive: {
    "&&&&": {
      color: theme.palette.error.main,
      borderColor: theme.palette.error.main,
      "&:hover, &:active": { color: theme.palette.error.main, borderColor: theme.palette.error.main },
    },
  },
}));

interface ConfirmationDialogProps {
  title: string;
  description?: React.ReactNode;
  footnote?: React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  onClickOutside?: () => void;
  destructive?: boolean;
  confirmDisabled?: boolean;
  cancelDisabled?: boolean;
  secondaryAction?: { label: string; onClick: () => void };
}

// 閉じる条件は呼び出し元が決める。確定後に自動で閉じたり、ESCで閉じたりしない。
export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  title,
  description,
  footnote,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  onClickOutside,
  destructive = false,
  confirmDisabled = false,
  cancelDisabled = false,
  secondaryAction,
}) => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  return (
    <StyledModal onClickOutside={onClickOutside} role="dialog" ariaLabel={title}>
      <Box className={classes.content}>
        <Box className={classes.head}>
          <Typography component="h2" className={classes.title}>
            {title}
          </Typography>
          {description && (
            <Typography component="div" className={classes.description}>
              {description}
            </Typography>
          )}
        </Box>
        {footnote && <Typography className={classes.footnote}>{footnote}</Typography>}
        <Box className={classes.actions}>
          <CustomButton
            tone={destructive ? "outline" : "primary"}
            buttonSize="L"
            fullWidth
            className={destructive ? classes.destructive : undefined}
            onClick={onConfirm}
            disabled={confirmDisabled}
          >
            {confirmLabel}
          </CustomButton>
          {secondaryAction && (
            <CustomButton tone="outline" buttonSize="L" fullWidth onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </CustomButton>
          )}
          <CustomButton
            tone="text"
            buttonSize={isMobile ? "M" : "L"}
            fullWidth
            onClick={onCancel}
            disabled={cancelDisabled}
          >
            {cancelLabel}
          </CustomButton>
        </Box>
      </Box>
    </StyledModal>
  );
};
